import { useState } from "react";
import axios from "axios";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import { motion, useReducedMotion } from "framer-motion";
import { FadeIn } from "@/components/FadeIn";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { CheckCircle2, Loader2 } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SIDE_IMAGE =
  "https://customer-assets.emergentagent.com/job_pond-education-hub/artifacts/g3ifbah6_784d63b8-ad21-41e6-b833-62762338d6bb.png";

const inputClasses =
  "h-12 rounded-none border-0 border-b border-[color:var(--apn-border)] bg-transparent px-0 text-base focus-visible:ring-0 focus-visible:border-[color:var(--apn-forest)] placeholder:text-foreground/35";

export const FinalCTA = () => {
  const [submitted, setSubmitted] = useState(false);
  const prefersReducedMotion = useReducedMotion();
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm({
    defaultValues: { name: "", email: "", phone: "", project: "" },
  });

  const onSubmit = async (values) => {
    try {
      await axios.post(`${API}/consultations`, {
        name: values.name,
        email: values.email,
        phone: values.phone || null,
        project_description: values.project,
      });
      setSubmitted(true);
      toast.success("Request received", {
        description: "We'll be in touch within two business days.",
      });
      reset();
    } catch (err) {
      const detail =
        err?.response?.data?.detail ||
        "Something went wrong sending your request. Please try again.";
      toast.error("Unable to send request", {
        description:
          typeof detail === "string" ? detail : "Please check your details and try again.",
      });
    }
  };

  return (
    <section id="book" data-testid="booking-section" className="bg-[color:var(--apn-paper-2)] overflow-hidden">
      <div className="grid grid-cols-1 lg:grid-cols-12">
        {/* Form chapter */}
        <div
          className="lg:col-span-7 pl-5 sm:pl-8 lg:pl-[max(2rem,calc((100vw-1280px)/2+2rem))] pr-5 sm:pr-8 lg:pr-20 py-[50px] lg:py-20"
          data-testid="final-cta-section"
        >
          <FadeIn>
            <h2 className="font-serif-heading font-medium text-4xl sm:text-5xl lg:text-[clamp(2.25rem,2.9vw+0.5rem,3.4rem)] leading-[1.05] tracking-[-0.015em] text-foreground">
              Start with a free conversation about your pond project
            </h2>
            <p className="mt-7 text-base sm:text-lg leading-relaxed text-foreground/65 max-w-[52ch]">
              Tell us about your project—we'll arrange
              <br className="lg:hidden" /> a free consultation.
            </p>

            <div className="mt-[41px] lg:mt-14 max-w-[620px]">
              {submitted ? (
                <motion.div
                  initial={prefersReducedMotion ? false : { opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4 }}
                  data-testid="consultation-form-success-message"
                  className="border-l-2 border-[color:var(--apn-forest)] pl-8 py-4"
                >
                  <CheckCircle2 className="h-9 w-9 text-[color:var(--apn-forest)]" aria-hidden="true" />
                  <h3 className="mt-5 font-serif-heading text-2xl sm:text-3xl font-medium text-foreground">
                    Thank you — your note is with us.
                  </h3>
                  <p className="mt-4 text-sm sm:text-base leading-relaxed text-foreground/60 max-w-[44ch]">
                    We'll read it carefully and reach out within two business
                    days to arrange your free introductory consultation.
                  </p>
                  <Button
                    data-testid="consultation-form-reset-button"
                    variant="outline"
                    onClick={() => setSubmitted(false)}
                    className="btn-lift mt-8 rounded-none border-[color:var(--apn-border)] bg-transparent text-foreground hover:bg-secondary/60 h-11 px-6"
                  >
                    Send another request
                  </Button>
                </motion.div>
              ) : (
                <form onSubmit={handleSubmit(onSubmit)} noValidate data-testid="consultation-form">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-10 gap-y-8">
                    <div className="space-y-2">
                      <Label htmlFor="apn-name" className="font-mono-meta text-[10px] text-foreground/55">
                        Name<span className="relative -top-[3px] ml-px" aria-hidden="true">*</span>
                      </Label>
                      <Input
                        id="apn-name"
                        data-testid="consultation-form-name-input"
                        placeholder="Your full name"
                        autoComplete="name"
                        className={inputClasses}
                        {...register("name", {
                          required: "Please tell us your name.",
                          minLength: { value: 2, message: "Name must be at least 2 characters." },
                        })}
                      />
                      {errors.name && (
                        <p data-testid="consultation-form-name-error" className="text-xs text-destructive">
                          {errors.name.message}
                        </p>
                      )}
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="apn-email" className="font-mono-meta text-[10px] text-foreground/55">
                        Email<span className="relative -top-[3px] ml-px" aria-hidden="true">*</span>
                      </Label>
                      <Input
                        id="apn-email"
                        type="email"
                        data-testid="consultation-form-email-input"
                        placeholder="you@example.com"
                        autoComplete="email"
                        className={inputClasses}
                        {...register("email", {
                          required: "Please provide your email address.",
                          pattern: {
                            value: /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/,
                            message: "Please enter a valid email address.",
                          },
                        })}
                      />
                      {errors.email && (
                        <p data-testid="consultation-form-email-error" className="text-xs text-destructive">
                          {errors.email.message}
                        </p>
                      )}
                    </div>
                  </div>

                  <div className="mt-8 space-y-2">
                    <Label htmlFor="apn-phone" className="font-mono-meta text-[10px] text-foreground/55">
                      Phone
                    </Label>
                    <Input
                      id="apn-phone"
                      type="tel"
                      data-testid="consultation-form-phone-input"
                      placeholder="(555) 555-5555"
                      autoComplete="tel"
                      className={inputClasses}
                      {...register("phone")}
                    />
                  </div>

                  <div className="mt-6 space-y-2">
                    <Label htmlFor="apn-project" className="font-mono-meta text-[10px] text-foreground/55">
                      Tell us about your project<span className="relative -top-[3px] ml-px" aria-hidden="true">*</span>
                    </Label>
                    <Textarea
                      id="apn-project"
                      data-testid="consultation-form-project-textarea"
                      placeholder="Where is your property? Do you have an existing pond or dugout? What are you hoping to do?"
                      rows={5}
                      className="rounded-none border-0 border-b border-[color:var(--apn-border)] bg-transparent px-0 text-base resize-y min-h-[120px] focus-visible:ring-0 focus-visible:border-[color:var(--apn-forest)] placeholder:text-foreground/35"
                      {...register("project", {
                        required: "Please share a few details about your project.",
                        minLength: {
                          value: 10,
                          message: "Please share a bit more detail (at least 10 characters).",
                        },
                      })}
                    />
                    {errors.project && (
                      <p data-testid="consultation-form-project-error" className="text-xs text-destructive">
                        {errors.project.message}
                      </p>
                    )}
                  </div>

                  <div className="mt-11 flex flex-col sm:flex-row sm:items-center gap-5">
                    <Button
                      type="submit"
                      data-testid="consultation-form-submit-button"
                      disabled={isSubmitting}
                      className="btn-lift rounded-none bg-[color:var(--apn-forest)] text-[color:var(--apn-paper-2)] hover:bg-[color:var(--apn-forest-2)] h-12 px-9 text-[15px]"
                    >
                      {isSubmitting ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />
                          Sending…
                        </>
                      ) : (
                        "Request free consultation"
                      )}
                    </Button>
                  </div>
                </form>
              )}
            </div>
          </FadeIn>
        </div>

        {/* Borderless image bleeding off the right edge */}
        <div className="lg:col-span-5 relative min-h-[46vh] lg:min-h-0">
          <FadeIn className="h-full">
            <div className="relative img-grade h-full min-h-[46vh] lg:absolute lg:inset-0">
              <img
                src={SIDE_IMAGE}
                alt="A consultant pointing across a prairie pond while talking with an Alberta landowner"
                className="editorial-img object-[28%_50%]"
                loading="lazy"
              />
            </div>
          </FadeIn>
        </div>
      </div>
    </section>
  );
};
